import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Check, Pencil } from 'lucide-react';
import QuestionCard, { type QuestionCardProps } from '../QuestionCard';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { SubmitButton } from '../questions/SubmitButton';
import { CompanionAvatar } from 'components/Companion';
import { FollowUpReplyInput } from './FollowUpReplyInput';
import { ReflectionCompletenessBar } from './ReflectionCompletenessBar';
import './FollowUpJournal.css';
import { QuestionLoadingSkeleton } from './QuestionLoadingSkeleton';
import { QuestionPrompt, QuestionText } from 'utils/questionMarkdown';
import { AnswerReferenceContext } from 'utils/answerReferences';
import type { Question, ResponsePayload } from 'types';
import { evaluationAnswer } from 'utils/answerEvaluation';
import { isQuestionGenerationConflict } from 'services/api/questionGenerationClient';
import {
  answerEvaluation,
  type AnswerEvaluationRequest,
  type AnswerEvaluationState,
} from 'services/api/answerEvaluationClient';

interface Props extends QuestionCardProps {
  flowName: string;
  generationId?: number;
  onBusyChange?: (busy: boolean) => void;
  onGenerationConflict?: (draft: ResponsePayload | null, reply?: string) => void;
  onServerResponses?: (responses: unknown[]) => void;
}

const requestId = () => typeof globalThis.crypto?.randomUUID === 'function'
  ? globalThis.crypto.randomUUID()
  : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const matchesOriginalAnswer = (question: Question, draft: ResponsePayload | null, saved: AnswerEvaluationState) => {
  const current = evaluationAnswer(question, draft);
  if (saved.outcome === 'skipped' && !current.text_value?.trim() && !current.text_list?.some((value) => value.trim())) return true;
  return JSON.stringify(current) === JSON.stringify(evaluationAnswer(question, { question: question.id!, ...saved.original_answer }));
};

/** Each mounted instance belongs to one run/item. The stage keys those identities. */
export const AiEvaluatedQuestion: React.FC<Props> = ({
  flowName, generationId, onBusyChange, onGenerationConflict, onServerResponses, onSubmit, response, ...cardProps
}) => {
  const { question, flowItem, flowRunId, isInteractionLocked, isSubmitting } = cardProps;
  const flowItemId = flowItem?.id ?? question.flow_item_id;
  const [state, setState] = useState<AnswerEvaluationState | null>(null);
  const [seedResponse, setSeedResponse] = useState<ResponsePayload | undefined>();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const answerReferenceContext = useContext(AnswerReferenceContext);
  // A REQ-106 conflict means the saved answer must be judged again against the
  // refreshed wording, never completed without evaluation.
  const [referenceConflict, setReferenceConflict] = useState(false);
  const referenceConflictRef = useRef(false);
  const [advancing, setAdvancing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [advanceFailure, setAdvanceFailure] = useState<'retry' | 'reload' | 'earlier' | null>(null);
  const [draft, setDraft] = useState<ResponsePayload | null>(null);
  const [reply, setReply] = useState('');
  const [failedRequest, setFailedRequest] = useState<AnswerEvaluationRequest | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [editingOriginal, setEditingOriginal] = useState(false);
  const [replyVoiceBusy, setReplyVoiceBusy] = useState(false);
  const mounted = useRef(true);
  const pending = useRef(false);
  const recoveryRequired = useRef(false);
  const draftSaveStarted = useRef(false);
  const pendingReplySlot = useRef<{ requestId: string; index: number; question: string } | null>(null);
  const advancingRef = useRef(false);
  const continueAfterEvaluation = useRef(false);
  const onServerResponsesRef = useRef(onServerResponses);
  const onSubmitRef = useRef(onSubmit);
  const latestDraftRef = useRef(draft);
  latestDraftRef.current = draft;
  onServerResponsesRef.current = onServerResponses;
  onSubmitRef.current = onSubmit;
  const followUpCard = useRef<HTMLDivElement>(null);
  const followUpHeading = useRef<HTMLHeadingElement>(null);
  const lastFocusedFollowUp = useRef<string | null>(null);
  const onDraftChangeRef = useRef(cardProps.onDraftChange);
  onDraftChangeRef.current = cardProps.onDraftChange;

  useEffect(() => {
    onBusyChange?.(busy || advancing || replyVoiceBusy || (state?.status === 'evaluating' && !error));
    return () => onBusyChange?.(false);
  }, [advancing, busy, error, onBusyChange, replyVoiceBusy, state?.status]);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const adopt = useCallback((next: AnswerEvaluationState) => {
    setState(next);
    if (next.response) onServerResponsesRef.current?.([next.response]);
  }, []);

  // Once accepted, retry only continuation, never the paid evaluation.
  const advance = useCallback(async (next: AnswerEvaluationState) => {
    if (!next.response?.is_completed || advancingRef.current) return;
    advancingRef.current = true;
    continueAfterEvaluation.current = false;
    setAdvancing(true);
    setAdvanceFailure(null);
    try {
      await onSubmitRef.current(next.response, { advance: true, serverPersisted: true });
    } catch (caught) {
      if (mounted.current) {
        const code = (caught as { code?: string })?.code;
        setAdvanceFailure(code === 'answer_evaluation_stale' ? 'reload' : code === 'earlier_answer_unsaved' ? 'earlier' : 'retry');
      }
    } finally {
      advancingRef.current = false;
      if (mounted.current) setAdvancing(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    if (!flowItemId || !flowRunId) {
      setError('Your answer could not be opened. Reload this page to try again.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setAdvanceFailure(null);
    setFailedRequest(null);
    void answerEvaluation.get(flowName, flowItemId, flowRunId).then(({ data }) => {
      if (!active) return;
      const initial = data.outcome === 'skipped' ? undefined : data.original_answer
        ? { question: question.id!, ...data.original_answer }
        : response;
      const initialAnswer = evaluationAnswer(question, initial);
      const emptyOptional = flowItem?.is_required === false && !initialAnswer.text_value?.trim()
        && !initialAnswer.text_list?.some((value) => value.trim());
      setSeedResponse(emptyOptional ? undefined : initial);
      setDraft(emptyOptional ? null : initial ?? null);
      setReply(data.retry_required ? data.turns[data.turns.length - 1]?.answer ?? '' : '');
      setEditingOriginal(false);
      // A revisit is for review; only this mounted view's explicit submission
      // authorizes automatic continuation when pending work settles.
      continueAfterEvaluation.current = false;
      adopt(data);
    }).catch(() => {
      if (active) setError('Your saved answer could not be loaded. Please try again.');
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
    // Adoption updates canonical responses; those updates must not reseed input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adopt, flowItemId, flowName, flowRunId, question.id, reloadToken]);

  const originalChanged = Boolean(state?.restart_required || (state?.original_answer && !matchesOriginalAnswer(question, draft, state)));
  const locked = Boolean(isInteractionLocked || isSubmitting || busy || advancing || loading || replyVoiceBusy || state?.status === 'evaluating');
  const canReply = state?.status === 'needs_follow_up' && Boolean(state.follow_up_question) && !originalChanged;
  const completed = state?.status === 'completed' && !originalChanged;
  const showConversation = Boolean(state?.turns.length) && !editingOriginal && !originalChanged;
  const activeFollowUpKey = canReply && state ? `${state.turns.length}:${state.follow_up_question}` : null;

  // Follow-ups retain the flow question id, so the stage cannot reset scrolling.
  // Focus and scroll once per new follow-up; never on keystrokes or draft saves.
  useEffect(() => {
    if (loading || !showConversation || !activeFollowUpKey || busy || failedRequest || lastFocusedFollowUp.current === activeFollowUpKey) return;
    const heading = followUpHeading.current;
    const card = followUpCard.current;
    if (!heading || !card) return;
    lastFocusedFollowUp.current = activeFollowUpKey;
    heading.focus({ preventScroll: true });
    const behavior = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    const root = document.getElementById('root');
    if (root) {
      // Keep the previous answer in view above the new prompt on the journal page.
      const top = Math.max(0, root.scrollTop + heading.getBoundingClientRect().top - root.getBoundingClientRect().top - 180);
      if (typeof root.scrollTo === 'function') root.scrollTo({ top, behavior });
      else root.scrollTop = top;
    } else {
      heading.scrollIntoView?.({ block: 'center', behavior });
    }
  }, [activeFollowUpKey, busy, failedRequest, loading, showConversation]);

  useEffect(() => {
    if (state?.status !== 'evaluating' || !flowItemId || !flowRunId) return;
    let active = true;
    const timer = setTimeout(() => {
      void answerEvaluation.get(flowName, flowItemId, flowRunId)
        .then(({ data }) => {
          if (!active) return;
          adopt(data);
          if (data.status === 'completed' && !data.turns.length && continueAfterEvaluation.current
            && matchesOriginalAnswer(question, latestDraftRef.current, data)) void advance(data);
        })
        .catch(() => { if (active) setError('We could not check your answer yet. Reload this page to resume.'); });
    }, 1500);
    return () => { active = false; clearTimeout(timer); };
  }, [adopt, advance, state, flowItemId, flowName, flowRunId, question]);

  const send = async (request: AnswerEvaluationRequest, recoverFirst = recoveryRequired.current) => {
    if (!flowItemId || pending.current) return;
    if (!recoverFirst && request.action === 'reply' && state?.follow_up_question) {
      pendingReplySlot.current = { requestId: request.request_id, index: state.turns.length - 1, question: state.follow_up_question };
    }
    pending.current = true;
    continueAfterEvaluation.current = true;
    setBusy(true);
    setError(null);
    setAdvanceFailure(null);
    // REQ-106: bind the evaluation to the earlier answers this card displayed.
    const referenceDigest = answerReferenceContext?.digest;
    const submit = (payload: AnswerEvaluationRequest) => answerEvaluation.submit(
      flowName, flowItemId, {
        ...payload,
        ...(generationId ? { generation_id: generationId } : {}),
        ...(referenceDigest ? { reference_digest: referenceDigest } : {}),
      },
    );
    const accept = async (data: AnswerEvaluationState, recovered = false) => {
      if (!mounted.current) return;
      adopt(data);
      recoveryRequired.current = false;
      setFailedRequest(null);
      // Recovery may already be showing the next prompt. Only the submitted
      // evidence slot can prove this reply was accepted; repeated text elsewhere cannot.
      const slot = pendingReplySlot.current;
      const savedTurn = slot ? data.turns[slot.index] : undefined;
      const replyAccepted = request.action === 'reply' && slot?.requestId === request.request_id
        && data.revision > request.expected_revision && savedTurn?.question === slot.question
        && savedTurn?.answer === request.reply;
      if (!recovered || !data.follow_up_question || replyAccepted) setReply('');
      pendingReplySlot.current = null;
      setEditingOriginal(false);
      if (data.status === 'completed' && (!data.turns.length || data.outcome === 'unavailable')
        && matchesOriginalAnswer(question, latestDraftRef.current, data)) await advance(data);
    };
    const recover = async () => {
      const { data } = await answerEvaluation.get(flowName, flowItemId, request.flow_run);
      if (!mounted.current) return;
      adopt(data);
      if (data.restart_required) {
        recoveryRequired.current = false;
        setFailedRequest(null);
        setError('This question changed. Review your answer before continuing.');
      } else if (data.original_answer && !matchesOriginalAnswer(question, latestDraftRef.current, data)) {
        recoveryRequired.current = false;
        setFailedRequest(null);
        setError('The saved answer changed. Review your answer before continuing.');
      } else if ((referenceConflictRef.current || data.outcome === 'references_changed')
        && data.original_answer && data.retry_required) {
        const result = await submit({ flow_run: request.flow_run, request_id: requestId(), expected_revision: data.revision,
          action: 'evaluate', answer: data.original_answer });
        referenceConflictRef.current = false;
        setReferenceConflict(false);
        await accept(result.data);
      } else if (data.original_answer && (data.status === 'evaluating' || data.retry_required)) {
        const result = await submit({ flow_run: request.flow_run, request_id: requestId(),
          expected_revision: data.revision, action: 'continue_without_evaluation' });
        await accept(result.data, true);
      } else if (data.status === 'not_started') {
        // Nothing was accepted. Submit only after obtaining the current revision.
        const result = await submit({ ...request, request_id: requestId(), expected_revision: data.revision });
        await accept(result.data);
      } else {
        await accept(data, true);
      }
    };
    try {
      if (recoverFirst) await recover();
      else await accept((await submit(request)).data);
    } catch (caught) {
      if (!mounted.current) return;
      if (isQuestionGenerationConflict(caught)) {
        onGenerationConflict?.(latestDraftRef.current, reply);
        setFailedRequest(null);
        setError('This question changed. Review your answer before starting a new attempt.');
        return;
      }
      const failure = caught as { response?: { status?: number; data?: { state?: AnswerEvaluationState; code?: string } } };
      const serverState = failure.response?.data?.state;
      if (serverState) adopt(serverState);
      if (failure.response?.data?.code === 'answer_references_changed') {
        // An earlier answer quoted here changed elsewhere: show the current
        // wording and keep the learner's draft for review.
        continueAfterEvaluation.current = false;
        referenceConflictRef.current = true;
        setReferenceConflict(true);
        answerReferenceContext?.refresh?.();
        setError('An earlier answer shown in this question changed. Review the updated question, then submit again.');
        return;
      }
      const conflict = failure.response?.status === 409;
      const unavailable = !failure.response || (failure.response.status ?? 0) >= 500;
      if (unavailable) recoveryRequired.current = true;
      if (!recoverFirst && unavailable) {
        try {
          await recover();
          return;
        } catch (recoveryFailure) {
          if (!mounted.current) return;
          if (isQuestionGenerationConflict(recoveryFailure)) {
            onGenerationConflict?.(latestDraftRef.current, reply);
            return;
          }
        }
      }
      continueAfterEvaluation.current = false;
      setFailedRequest(conflict ? null : request);
      setError(conflict
        ? 'This answer changed in another window. Review the saved answers before continuing.'
        : 'We could not finish just yet. Your answer is still here. Please try again.');
    } finally {
      pending.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  const handleSubmit: QuestionCardProps['onSubmit'] = async (answer, options) => {
    if (!state || !flowRunId || pending.current || locked) return;
    if (options?.advance === false) {
      if (state.status === 'not_started' && !state.original_answer && !recoveryRequired.current) {
        draftSaveStarted.current = true;
        await onSubmit({ ...answer, is_completed: false }, options);
      }
      return;
    }
    if (completed && state.response?.is_completed) {
      await advance(state);
      return;
    }
    if (editingOriginal && !originalChanged && state.turns.length) {
      setEditingOriginal(false);
      return;
    }
    const evidence = evaluationAnswer(question, answer);
    const hasEvidence = Boolean(evidence.text_value?.trim() || evidence.text_list?.some((value) => value.trim()));
    if (recoveryRequired.current) {
      const skip = flowItem?.is_required === false && !state.original_answer && !hasEvidence;
      await send({ flow_run: flowRunId, request_id: requestId(), expected_revision: state.revision,
        ...(skip ? { action: 'skip' as const } : { action: originalChanged ? 'restart' as const : 'evaluate' as const, answer: evidence }) }, true);
      return;
    }
    if (flowItem?.is_required === false && !state.original_answer && !hasEvidence) {
      // Clearing an input is a draft mutation too. Drain prior saves and clear
      // the persisted draft before asking the server to authorize an empty skip.
      if (response || seedResponse || draftSaveStarted.current) {
        setBusy(true);
        try {
          await onSubmit({ ...answer, ...(question.question_type === 'text_list' ? { text_list: [] } : { text_value: '' }), is_completed: false }, { advance: false, persistImmediately: true, propagateErrors: true });
        } catch (caught) {
          if (isQuestionGenerationConflict(caught)) onGenerationConflict?.(latestDraftRef.current, reply);
          else setError('Your answer could not be saved. Please try again.');
          return;
        } finally {
          if (mounted.current) setBusy(false);
        }
        if (!mounted.current) return;
      }
      await send({ flow_run: flowRunId, request_id: requestId(), expected_revision: state.revision, action: 'skip' });
      return;
    }
    const textLength = evidence.text_value?.length ?? evidence.text_list?.reduce((length, value) => length + value.length, 0) ?? 0;
    if (textLength > 16000) {
      setError('Please keep your original answer to 16,000 characters or fewer.');
      return;
    }
    if (state.status === 'not_started' && !state.original_answer) {
      setBusy(true);
      try {
        // Drain ordinary draft saves before evaluation owns the canonical row.
        await onSubmit({ ...answer, is_completed: false }, { advance: false, persistImmediately: true, propagateErrors: true });
      } catch (caught) {
        setBusy(false);
        if (isQuestionGenerationConflict(caught)) {
          onGenerationConflict?.(latestDraftRef.current, reply);
          return;
        }
        setError('Your answer could not be saved. Please try again.');
        return;
      }
      if (!mounted.current) return;
      setBusy(false);
    }
    await send({ flow_run: flowRunId, request_id: requestId(), expected_revision: state.revision,
      action: originalChanged ? 'restart' : 'evaluate', answer: evidence });
  };

  const handleDraftChange = useCallback((next: ResponsePayload | null) => {
    setDraft(next);
    onDraftChangeRef.current?.(next);
    setFailedRequest(null);
    setError(null);
    setAdvanceFailure(null);
  }, []);

  if (loading) return <QuestionLoadingSkeleton question={question} loadingLabel="Loading your reflection" />;
  if (!state) return (
    <div data-flow-surface="card" className="w-full max-w-xl rounded-3xl border border-border/40 bg-white/80 p-8" aria-live="polite">
        <p role="alert">{error}</p>
        <Button className="mt-4" variant="secondary" onClick={() => setReloadToken((value) => value + 1)}>Try again</Button>
    </div>
  );

  const needsRetry = !originalChanged && !completed
    && (state.retry_required || (!canReply && state.status === 'needs_follow_up'));
  const retry = () => send(failedRequest ?? { flow_run: flowRunId!, request_id: requestId(), expected_revision: state.revision,
    action: 'evaluate', answer: state.original_answer ?? evaluationAnswer(question, draft) }, true);
  const savedOriginal: ResponsePayload = { question: question.id!, ...state.original_answer };
  const beginEditing = () => {
    setSeedResponse(savedOriginal);
    setDraft(savedOriginal);
    setEditingOriginal(true);
    setAdvanceFailure(null);
    setError(null);
  };
  const errors = <>
    {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
    {error && state.status === 'evaluating' && !busy && <Button type="button" variant="secondary" onClick={failedRequest ? retry : () => setReloadToken((value) => value + 1)}>{failedRequest ? 'Continue with saved answer' : 'Check saved answer'}</Button>}
    {advanceFailure && <p role="alert" className="text-sm text-destructive">{advanceFailure === 'reload'
      ? 'This reflection changed in another window. Reload the saved question before continuing.'
      : advanceFailure === 'earlier'
        ? 'Your answer is saved, but an earlier answer could not be saved. Go back to it and try again.'
        : 'Your answer is saved, but we could not continue. Please try again.'}</p>}
    {advanceFailure === 'reload' && <Button type="button" variant="secondary" onClick={() => setReloadToken((value) => value + 1)}>Reload question</Button>}
  </>;

  if (!showConversation) {
    const hasExtraContent = Boolean(error || advanceFailure || editingOriginal || originalChanged || failedRequest || needsRetry);
    return <QuestionCard {...cardProps}
      generationId={generationId}
      allowClearedOptionalSkip
      response={seedResponse}
      onSubmit={handleSubmit}
      onDraftChange={handleDraftChange}
      disableAutoSync={state.status !== 'not_started' || Boolean(state.original_answer) || busy}
      isInteractionLocked={locked}
      isSubmitLoading={busy || advancing || state.status === 'evaluating'}
      hideSubmit={advanceFailure === 'reload' || (!originalChanged && Boolean(failedRequest || needsRetry))}
      afterResponse={hasExtraContent ? <div className="space-y-3">
        {originalChanged && <p className="text-sm text-muted-foreground">{state.restart_required
          ? 'The guidance for this question has changed. Continue to start a fresh reflection.'
          : 'Continue with this answer to start a fresh reflection.'}</p>}
        {editingOriginal && <Button type="button" variant="ghost" size="auto" disabled={locked} onClick={() => {
          setDraft(savedOriginal); setEditingOriginal(false); setError(null);
        }}>Cancel editing</Button>}
        {errors}
        {(failedRequest || needsRetry) && !originalChanged && !completed && <Button type="button" variant="secondary" disabled={locked} onClick={retry}>{referenceConflict || state.outcome === 'references_changed' ? 'Check my answer again' : 'Continue with saved answer'}</Button>}
      </div> : undefined}
    />;
  }

  const currentTurn = state.turns[state.turns.length - 1];
  const displayedQuestion = state.follow_up_question ?? currentTurn?.question;
  const savedReply = needsRetry ? currentTurn?.answer ?? '' : '';
  const canCompose = !completed && !needsRetry;
  const unsentReply = error && reply.trim() && !canReply && currentTurn?.answer !== reply;
  const evaluating = busy || state.status === 'evaluating';
  const original = state.original_answer;
  // A reply that is being read belongs on the page, above Wise Friend's thinking line.
  const pendingReply = evaluating && !error && reply.trim() && currentTurn?.answer == null ? reply : null;
  // The active prompt is the one awaiting a reply; earlier prompts read as history.
  const activeIndex = !completed && !evaluating && currentTurn && currentTurn.answer == null ? state.turns.length - 1 : -1;
  const entry = (text: string, key: string) => <p key={key} className="follow-up-journal-entry whitespace-pre-wrap break-words text-foreground">{text}</p>;
  return <div className="flex w-full justify-center">
    <Card ref={followUpCard} padding="none" maxWidth="none" variant="glass" disableAnimation
      className="w-full min-w-0 max-w-[calc(100vw-3.25rem)] overflow-hidden px-6 pb-6 pt-7 sm:max-w-[44rem] sm:px-12 sm:pt-9"
      data-testid="follow-up-card" data-flow-surface="card" aria-busy={evaluating || advancing}>
      <div role="log" aria-label="Question and answer conversation" aria-live="off" data-testid="follow-up-conversation" data-swipe-ignore="true">
        {question.header && <div className="text-xs font-semibold uppercase text-muted-foreground">
          <QuestionText text={question.header} citations={question.citations} inline answerReferences />
        </div>}
        <div className={question.header ? 'pt-2' : undefined}><QuestionPrompt text={question.text} citations={question.citations} /></div>
        {question.subtext && <div className="pt-3 text-[length:var(--body-m-font-size)] leading-relaxed text-foreground">
          <QuestionText text={question.subtext} citations={question.citations} answerReferences />
        </div>}
        <div className="mt-6"><ReflectionCompletenessBar completeness={state.completeness}
          evaluating={evaluating && !error} outcome={completed && state.outcome !== 'references_changed' ? state.outcome : null} /></div>
        <div className="mt-6 space-y-6">
          <div className="group relative pr-8 sm:pr-0">
            <span className="sr-only">Your original answer</span>
            {original?.text_list ? <ol className="follow-up-journal-entry list-decimal space-y-1 pl-5 text-foreground">
              {original.text_list.map((text, index) => <li key={index} className="whitespace-pre-wrap break-words">{text}</li>)}
            </ol> : entry(original?.text_value ?? '', 'original')}
            <Button type="button" variant="ghost" size="auto" aria-label="Edit original answer" disabled={locked}
              className="absolute -right-1.5 top-0.5 rounded-full p-1.5 text-muted-foreground opacity-70 hover:opacity-100 sm:-right-9"
              onClick={beginEditing}><Pencil aria-hidden="true" className="h-3.5 w-3.5" /></Button>
          </div>
          {state.turns.map((turn, index) => {
            const active = index === activeIndex;
            const latest = index === state.turns.length - 1;
            const prompt = <span className="follow-up-journal-prompt whitespace-pre-wrap break-words">
              {active ? displayedQuestion : turn.question}
            </span>;
            return <React.Fragment key={index}>
              <div className={`flex items-start gap-3${latest ? ' follow-up-journal-new' : ''}`}>
                <CompanionAvatar size={26} label="Wise Friend asks" className="mt-0.5 shrink-0" />
                {active ? <h2 id={`evaluation-question-${question.id}`} ref={followUpHeading} tabIndex={-1}
                  className="text-[1.12rem] font-normal leading-relaxed focus:outline-none">{prompt}</h2>
                  : <p className="text-[1.08rem] leading-relaxed">{prompt}</p>}
              </div>
              {turn.answer != null && <div><span className="sr-only">Your reply</span>{entry(turn.answer, `answer-${index}`)}</div>}
            </React.Fragment>;
          })}
          {pendingReply && <div className="follow-up-journal-new"><span className="sr-only">Your reply</span>{entry(pendingReply, 'pending-reply')}</div>}
          {evaluating && !error && <div role="status" className="follow-up-journal-new flex items-start gap-3">
            <CompanionAvatar size={26} mood="thinking" className="mt-0.5 shrink-0" />
            <p className="follow-up-journal-prompt text-[1.08rem] leading-relaxed">Thinking about what you wrote…</p>
          </div>}
        </div>
      </div>
      {completed && <div role="status" className="follow-up-journal-new mt-6 flex items-center gap-2 text-muted-foreground">
        <Check aria-hidden="true" className="h-4 w-4 text-emerald-700" />
        <h2 className="text-sm font-medium">{state.outcome === 'criteria_met' ? 'Your reflection is complete' : 'Your reflection is saved'}</h2>
      </div>}
      <div className="space-y-3 pt-4" data-swipe-ignore="true">
        {!completed && !pendingReply && <FollowUpReplyInput key={`${state.turns.length}:${currentTurn?.question}`}
          id={`evaluation-reply-${question.id}`} value={reply || savedReply} onChange={setReply}
          disabled={locked || Boolean(failedRequest) || !canCompose}
          describedBy={displayedQuestion && !evaluating ? `evaluation-question-${question.id}` : undefined}
          onBusyChange={setReplyVoiceBusy} />}
        {unsentReply && <div className="space-y-1 rounded-2xl border border-border/50 p-3">
          <p className="text-xs font-semibold text-muted-foreground">Your unsent follow-up</p>
          <p className="whitespace-pre-wrap break-words text-sm">{reply}</p>
        </div>}
        {errors}
        <div className="flex justify-start" data-testid="follow-up-actions">{advanceFailure !== 'reload' && <SubmitButton
          onSubmit={completed ? () => advance(state) : failedRequest || needsRetry ? retry
            : () => send({ flow_run: flowRunId!, request_id: requestId(), expected_revision: state.revision, action: 'reply', reply })}
          isDisabled={locked || (!completed && !failedRequest && !needsRetry && (!reply.trim() || reply.length > 16000))}
          isLoading={evaluating || advancing}
          buttonText={failedRequest || needsRetry ? (referenceConflict || state.outcome === 'references_changed' ? 'Check my answer again' : 'Continue with saved answer') : completed ? 'Continue' : 'Add to reflection'} />}</div>
      </div>
    </Card>
  </div>;
};
